import React from 'react';
import { Button, Buildings, Card, CardContent, Flex, Heading, Text, TextField } from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { suggestWorkspaceName } from './workspaceName';

/**
 * Signup "Name your workspace" screen: shown in place of the dead end for a
 * signed-in user with no organization, while they may create one
 * (`user.canCreateOrganization`). Continue creates the org and switches into it,
 * returning to the current URL so a `/cli-login?user_code=…` visit lands back
 * on the approve page.
 */
export function NameWorkspace() {
  const { user, createOrg, isCreatingOrg, isSwitchingOrg, signOut } = useAuth();
  const [name, setName] = React.useState(() =>
    suggestWorkspaceName(user?.email ?? null, user?.name ?? null)
  );
  const trimmed = name.trim();
  const isBusy = isCreatingOrg || isSwitchingOrg;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!trimmed || isBusy) return;
    createOrg({
      name: trimmed,
      first: true,
      redirectUrl: window.location.pathname + window.location.search,
    });
  };

  return (
    <Flex align="center" justify="center" className="min-h-screen bg-background">
      <Card padding="lg" className="max-w-md w-full mx-4">
        <CardContent>
          <form onSubmit={submit}>
            <Flex direction="column" gap="lg">
              <Flex direction="column" align="center" gap="sm" className="text-center">
                <div className="p-3 bg-primary/10 rounded-full">
                  <Buildings size={40} className="text-primary" />
                </div>
                <Heading level={2}>Name your workspace</Heading>
                <Text size="sm" className="text-muted-foreground text-pretty">
                  This is where your apps, tests and billing live. You can rename it later in
                  Settings.
                </Text>
              </Flex>
              <TextField
                label="Workspace name"
                value={name}
                onChange={setName}
                maxLength={80}
                autoFocus
              />
              <Flex direction="column" gap="sm">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  isDisabled={!trimmed || isBusy}
                >
                  {isBusy ? 'Creating...' : 'Continue'}
                </Button>
                <Button variant="ghost" className="w-full" onPress={() => signOut()}>
                  Sign out
                </Button>
              </Flex>
            </Flex>
          </form>
        </CardContent>
      </Card>
    </Flex>
  );
}
